import { ChangeDetectionStrategy, Component } from '@angular/core';
import { RouterLink } from '@angular/router';
import { RelatedProductsService } from '@df/catalog-ui/related-products/related-products.service';
import { ZippyModule } from '@df/legacy/ui/zippy/zippy.module';
import { AdvancedCommerce } from '@df/module-advanced-commerce';
import { SizeClassDirective } from '@df/ui/common/size-class.directive';
import { DfUiModule } from '@df/ui/df-ui.module';
import { ICmsClientModule } from '@icms/client/icms-client.module';
import { ICmsComponent, NICmsContentComponent, NICmsContentField } from '@icms/core/content';
import find from 'lodash-es/find';
import orderBy from 'lodash-es/orderBy';
import remove from 'lodash-es/remove';
import { RelatedProductsComponent } from '../../../related-products/related-products.component';
import { AbstractProductCmsComponent } from '../abstract-product-cms.component';
// eslint-disable-next-line @typescript-eslint/no-namespace

let uid = 0;
export namespace NCmsRelatedProducts {
    export interface IComponentData extends NICmsContentComponent.IData {
        title: string;
        subtitle: string;
        sections: ISection[];
    }
    export interface ISection {
        /**
         * Section title
         */
        title: string;
        /**
         * Max items qty
         */
        limit: number;
        /**
         * Engines list
         */
        engines: IEngine[];
    }
    export interface IEngine {
        type: string;
    }
    export interface ISections {
        section: ISection;
        position: number;
        visible: boolean;
    }
}
@Component({
    selector: 'cms-related-products',
    templateUrl: './cms-related-products.html',
    standalone: true,
    imports: [SizeClassDirective, ICmsClientModule, ZippyModule, DfUiModule, RouterLink, RelatedProductsComponent],
    changeDetection: ChangeDetectionStrategy.OnPush
})
@ICmsComponent<CmsRelatedProductsComponent>({
    id: 'related-products',
    name: 'Related products',
    tags: ['product page', 'listing', 'carousel'],
    onDemandOnly: true,
    hidePreview: true,
    fields: [
        <NICmsContentField.IField>{
            type: NICmsContentField.EFieldType.label,
            key: 'section-general',
            title: 'General'
        },
        <NICmsContentField.IField>{
            key: 'title',
            type: NICmsContentField.EFieldType.text,
            title: 'Main title',
            default: 'Recommended for you',
            required: true
        },
        <NICmsContentField.IField>{
            key: 'subtitle',
            type: NICmsContentField.EFieldType.text,
            title: 'Subtitle',
            default: 'Products we recommended for you',
            required: false
        },
        <NICmsContentField.IRepeaterField>{
            type: NICmsContentField.EFieldType.repeater,
            key: 'sections',
            title: 'Sections',
            itemName: 'Section',
            itemNameField: 'title',
            defaultSize: 1,
            fields: [
                <NICmsContentField.IField>{
                    key: 'title',
                    type: NICmsContentField.EFieldType.text,
                    title: 'Section title',
                    default: 'You may also like',
                    required: true
                },
                <NICmsContentField.IField>{
                    key: 'limit',
                    type: NICmsContentField.EFieldType.number,
                    title: 'Max products qty',
                    required: true,
                    limit: 4
                },
                <NICmsContentField.IField>{
                    key: 'cta_copy',
                    type: NICmsContentField.EFieldType.text,
                    title: 'Button copy'
                },
                <NICmsContentField.IField>{
                    key: 'cta_link',
                    type: NICmsContentField.EFieldType.text,
                    title: 'Button link'
                },
                <NICmsContentField.IRepeaterField>{
                    type: NICmsContentField.EFieldType.repeater,
                    key: 'engines',
                    title: 'Engines',
                    itemName: 'Engine',
                    itemNameField: 'id',
                    required: true,
                    defaultSize: 1,
                    fields: [
                        <NICmsContentField.IChoiceField>{
                            key: 'type',
                            type: NICmsContentField.EFieldType.choice,
                            title: 'Type',
                            required: true,
                            options: () =>
                                Object.keys(RelatedProductsService.engines)
                                    .filter(e => e !== AdvancedCommerce.Engine.Id.recommendations)
                                    .map(i => ({ key: i, title: i })),
                            default: 'category'
                        }
                    ]
                }
            ]
        }
    ]
})
export class CmsRelatedProductsComponent extends AbstractProductCmsComponent<NCmsRelatedProducts.IComponentData> {
    private _sections: NCmsRelatedProducts.ISections[] = [];
    /**
     * List of visible section (ones that do have any products to show)
     */
    visibleSections: NCmsRelatedProducts.ISection[] = [];
    protected uid = uid++;
    /**
     * Retrieves related product component options
     */
    getOptions(section: NCmsRelatedProducts.ISection) {
        return {
            engines: section.engines.map(i => {
                return {
                    id: i.type,
                    options: {
                        limit: section.limit
                    }
                };
            })
        };
    }
    /**
     * If componentn uses multiple sections
     */
    get hasManySections(): boolean {
        return this.visibleSections.length > 1;
    }
    onPrinted(hasModels: boolean, section: NCmsRelatedProducts.ISection, position = 0) {
        const exists = find(this._sections, (item: NCmsRelatedProducts.ISections) => item.section === section);
        if (hasModels && !exists) {
            this._sections.push({
                section,
                position,
                visible: hasModels
            });
        } else if (!hasModels && exists) {
            this._sections = this._sections.filter((item: NCmsRelatedProducts.ISections) => item.position === position);
        }
        this.visibleSections = orderBy(this._sections, 'position').map(item => item.section);
    }
    isVisible(section: NCmsRelatedProducts.ISection): boolean {
        return this.visibleSections.includes(section);
    }
    sectionTrackFn(index: number) {
        return index;
    }
}
